import {
  SKETCH_EXTENT,
  type FloorPlanSketch,
  type SketchArea,
  type SketchAreaKind,
  type SketchDesk,
  type SketchLabel,
  type SketchPoint,
  type SketchRoom,
  type SketchWall,
  type SketchWallKind,
} from '../models/floorplan-sketch';
import { SYMBOL_ICONS, SYMBOL_KINDS, type SymbolKind } from './symbol-shapes';

/**
 * The step between reading an image and making a plan of it: what the tracer found, as a list the
 * administrator goes through and corrects.
 *
 * Every find is one item with a kind and an outline, whatever it was found as. That is what makes
 * correcting it one gesture: a shape read as furniture that is really a desk only needs its kind
 * changed, and everything a desk needs — where it is, which way it faces, how big it is — is read
 * off the outline when the plan is made. Things the tracer missed are marked by drawing a box.
 *
 * Coordinates are the sketch's: 0 to 1000 across and down the (straightened) picture. `aspect`,
 * its height over its width, is what turns them into lengths and angles.
 */

export type ReviewKind = 'room' | 'desk' | 'furniture' | 'door' | 'window' | 'stairs' | 'column' | 'wall' | 'label' | SymbolKind;

/** In the order they are offered: the kinds that matter most for booking first, the fittings last. */
export const REVIEW_KINDS: readonly ReviewKind[] = [
  'desk', 'room', 'label', 'furniture', 'wall', 'door', 'window', 'stairs', 'column', ...SYMBOL_KINDS,
];

export const isSymbolKind = (kind: ReviewKind): kind is SymbolKind => (SYMBOL_KINDS as readonly ReviewKind[]).includes(kind);

export interface ReviewItem {
  readonly id: string;
  readonly kind: ReviewKind;
  /** The shape on the picture, in sketch coordinates. */
  readonly outline: readonly SketchPoint[];
  /** A label's words. Kept when the kind changes, so changing it back loses nothing. */
  readonly text: string;
  /** For a wall: where it stands, and whether it is drawn with a thickness of its own. */
  readonly wallKind?: SketchWallKind;
  readonly thick?: boolean;
  /** How sure the tracer was, 0 to 1. Absent for what the administrator drew or has changed. */
  readonly confidence?: number;
}

/** Below this, a find is shown as one to check. */
export const UNCERTAIN_BELOW = 0.6;

export const isUncertain = (item: ReviewItem): boolean => item.confidence !== undefined && item.confidence < UNCERTAIN_BELOW;

/** A turned rectangle, in lengths of `x` (a thousandth of the picture's width), y down. */
export interface ReviewBox {
  readonly centre: SketchPoint;
  readonly length: number;
  readonly breadth: number;
  /** The long side's direction as the picture is drawn: clockwise, y down. */
  readonly imageDegrees: number;
}

const RADIANS = Math.PI / 180;

let lastId = 0;
const newId = (): string => {
  lastId += 1;
  return `review-${lastId}`;
};

const round = (value: number): number => Math.round(value * 100) / 100;

/** A sketch point as a length-true point: `y` stretched back by the picture's proportions. */
const toSpace = (point: SketchPoint, aspect: number): [number, number] => [point.x, point.y * aspect];

const fromSpace = ([x, y]: [number, number], aspect: number): SketchPoint => ({ x: round(x), y: round(y / aspect) });

/** The four corners of a turned rectangle, in sketch coordinates. */
export const cornersOfBox = (box: ReviewBox, aspect: number): SketchPoint[] => {
  const [cx, cy] = toSpace(box.centre, aspect);
  const [ux, uy] = [Math.cos(box.imageDegrees * RADIANS), Math.sin(box.imageDegrees * RADIANS)];
  const [vx, vy] = [-uy, ux];
  const [halfLength, halfBreadth] = [box.length / 2, box.breadth / 2];

  return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([su, sv]) => fromSpace([
    cx + (su * halfLength * ux) + (sv * halfBreadth * vx),
    cy + (su * halfLength * uy) + (sv * halfBreadth * vy),
  ], aspect));
};

/**
 * The smallest turned rectangle round an outline, trying the direction of each of its edges —
 * which for any rectangle, and near enough for any drawn shape, is the answer.
 */
export const boxOfOutline = (outline: readonly SketchPoint[], aspect: number): ReviewBox => {
  const points = outline.map((point) => toSpace(point, aspect));
  const directions = points.length > 1
    ? points.map(([x, y], index) => {
      const [nx, ny] = points[(index + 1) % points.length];
      return Math.atan2(ny - y, nx - x);
    })
    : [0];

  let best: ReviewBox | undefined;
  let bestArea = Infinity;

  [0, ...directions].forEach((radians) => {
    const [ux, uy] = [Math.cos(radians), Math.sin(radians)];
    const us = points.map(([x, y]) => (x * ux) + (y * uy));
    const vs = points.map(([x, y]) => (y * ux) - (x * uy));
    const [minU, maxU, minV, maxV] = [Math.min(...us), Math.max(...us), Math.min(...vs), Math.max(...vs)];
    const area = (maxU - minU) * (maxV - minV);

    if (area < bestArea - 1e-9) {
      bestArea = area;
      const [mu, mv] = [(minU + maxU) / 2, (minV + maxV) / 2];
      const along = maxU - minU;
      const across = maxV - minV;
      const degrees = radians / RADIANS;
      best = {
        centre: fromSpace([(mu * ux) - (mv * uy), (mu * uy) + (mv * ux)], aspect),
        length: Math.max(along, across),
        breadth: Math.min(along, across),
        imageDegrees: along >= across ? degrees : degrees + 90,
      };
    }
  });

  return best as ReviewBox;
};

/** A desk's direction in the plan (counter-clockwise, y up), from 90 to -90, square when nearly so. */
const planDegreesOf = (imageDegrees: number): number => {
  let degrees = -imageDegrees;
  while (degrees <= -90) degrees += 180;
  while (degrees > 90) degrees -= 180;

  if (Math.abs(degrees) < 2) return 0;
  if (Math.abs(Math.abs(degrees) - 90) < 2) return 90;
  return Math.round(degrees * 10) / 10;
};

/** How big a desk is on this picture, in lengths of `x`, for a desk marked with a click. */
export const deskSizeOf = (sketch: FloorPlanSketch): { length: number; breadth: number } => {
  const drawn = sketch.desks.filter((desk) => desk.width && desk.depth);

  if (drawn.length) {
    const lengths = drawn.map((desk) => desk.width as number).sort((a, b) => a - b);
    const depths = drawn.map((desk) => desk.depth as number).sort((a, b) => a - b);
    return { length: lengths[Math.floor(lengths.length / 2)], breadth: depths[Math.floor(depths.length / 2)] };
  }

  if (sketch.metresAcrossImage && sketch.metresAcrossImage > 0) {
    const perMetre = SKETCH_EXTENT / sketch.metresAcrossImage;
    return { length: 1.6 * perMetre, breadth: 0.8 * perMetre };
  }

  return { length: 40, breadth: 20 };
};

/** The rectangle a wall covers: its thickness, or a thin line when it is drawn as a line. */
const wallOutline = (wall: SketchWall, aspect: number): SketchPoint[] => {
  const [fx, fy] = toSpace(wall.from, aspect);
  const [tx, ty] = toSpace(wall.to, aspect);
  const length = Math.hypot(tx - fx, ty - fy);

  return cornersOfBox({
    centre: fromSpace([(fx + tx) / 2, (fy + ty) / 2], aspect),
    length,
    breadth: wall.thickness && wall.thickness > 0 ? wall.thickness : 2,
    imageDegrees: Math.atan2(ty - fy, tx - fx) / RADIANS,
  }, aspect);
};

const labelOutline = (label: SketchLabel, aspect: number): SketchPoint[] => {
  if (label.box) {
    const {
      left, top, right, bottom,
    } = label.box;
    return [{ x: left, y: top }, { x: right, y: top }, { x: right, y: bottom }, { x: left, y: bottom }];
  }

  return cornersOfBox({
    centre: { x: label.x, y: label.y }, length: 40, breadth: 12, imageDegrees: 0,
  }, aspect);
};

/** Everything the tracer found, one item each. Stairs' and fittings' icons come back with them. */
export const reviewItemsOf = (sketch: FloorPlanSketch, aspect: number): ReviewItem[] => {
  const size = deskSizeOf(sketch);

  return [
    ...sketch.rooms.map((room): ReviewItem => ({
      id: newId(), kind: 'room', outline: room.outline, text: room.name ?? '', confidence: room.confidence,
    })),
    ...sketch.desks.map((desk): ReviewItem => ({
      id: newId(),
      kind: 'desk',
      outline: cornersOfBox({
        centre: { x: desk.x, y: desk.y },
        length: desk.width ?? size.length,
        breadth: desk.depth ?? size.breadth,
        imageDegrees: -desk.rotationDegrees,
      }, aspect),
      text: '',
      confidence: desk.confidence,
    })),
    ...sketch.walls.map((wall): ReviewItem => ({
      id: newId(),
      kind: 'wall',
      outline: wallOutline(wall, aspect),
      text: '',
      wallKind: wall.kind ?? 'partition',
      thick: !!wall.thickness,
      confidence: wall.confidence,
    })),
    ...(sketch.areas ?? []).map((area): ReviewItem => ({
      id: newId(), kind: area.kind, outline: area.outline, text: '', confidence: area.confidence,
    })),
    ...sketch.labels.map((label): ReviewItem => ({
      id: newId(), kind: 'label', outline: labelOutline(label, aspect), text: label.text, confidence: label.confidence,
    })),
  ];
};

/** The item as another kind. Nothing else about it changes, so the change can be undone. */
export const withKind = (item: ReviewItem, kind: ReviewKind): ReviewItem => ({
  ...item,
  kind,
  // Somebody has looked at it and said what it is.
  confidence: undefined,
  wallKind: kind === 'wall' ? item.wallKind ?? 'partition' : item.wallKind,
  // A shape made a wall is a wall as thick as the shape.
  thick: kind === 'wall' && item.kind !== 'wall' ? true : item.thick,
});

/** The item turned a quarter round its middle: a desk facing the other way. */
export const turnedQuarter = (item: ReviewItem, aspect: number): ReviewItem => {
  const box = boxOfOutline(item.outline, aspect);
  const [cx, cy] = toSpace(box.centre, aspect);

  return {
    ...item,
    outline: item.outline.map((point) => {
      const [x, y] = toSpace(point, aspect);
      return fromSpace([cx - (y - cy), cy + (x - cx)], aspect);
    }),
  };
};

/**
 * Something the tracer missed, marked by dragging a box over it — or, for a desk, by clicking
 * where it is: a click makes a desk the size of the others.
 */
export const markedItem = (
  kind: ReviewKind,
  from: SketchPoint,
  to: SketchPoint,
  aspect: number,
  deskSize: { length: number; breadth: number },
): ReviewItem | undefined => {
  const [fx, fy] = toSpace(from, aspect);
  const [tx, ty] = toSpace(to, aspect);
  const clicked = Math.abs(tx - fx) < 4 && Math.abs(ty - fy) < 4;

  if (clicked && kind !== 'desk' && kind !== 'label') {
    return undefined;
  }

  if (clicked) {
    const size = kind === 'desk' ? deskSize : { length: 40, breadth: 12 };
    return {
      id: newId(),
      kind,
      outline: cornersOfBox({
        centre: from, length: size.length, breadth: size.breadth, imageDegrees: 0,
      }, aspect),
      text: '',
      wallKind: kind === 'wall' ? 'partition' : undefined,
      thick: kind === 'wall' ? true : undefined,
    };
  }

  const [left, right] = [Math.min(from.x, to.x), Math.max(from.x, to.x)];
  const [top, bottom] = [Math.min(from.y, to.y), Math.max(from.y, to.y)];

  return {
    id: newId(),
    kind,
    outline: [{ x: left, y: top }, { x: right, y: top }, { x: right, y: bottom }, { x: left, y: bottom }],
    text: '',
    wallKind: kind === 'wall' ? 'partition' : undefined,
    thick: kind === 'wall' ? true : undefined,
  };
};

const centreOf = (outline: readonly SketchPoint[]): SketchPoint => ({
  x: round(outline.reduce((total, point) => total + point.x, 0) / outline.length),
  y: round(outline.reduce((total, point) => total + point.y, 0) / outline.length),
});

/** How many of each kind there are. */
export const countsOf = (items: readonly ReviewItem[]): Record<ReviewKind, number> => {
  const counts = Object.fromEntries(REVIEW_KINDS.map((kind) => [kind, 0])) as Record<ReviewKind, number>;
  items.forEach((item) => { counts[item.kind] += 1; });
  return counts;
};

/** The corrected sketch: what the plan is made from. The building's outline and size are the tracer's. */
export const sketchOfReview = (base: FloorPlanSketch, items: readonly ReviewItem[], aspect: number): FloorPlanSketch => {
  const rooms: SketchRoom[] = [];
  const desks: SketchDesk[] = [];
  const walls: SketchWall[] = [];
  const areas: SketchArea[] = [];
  const labels: SketchLabel[] = [];
  const icons: { iconName: string; x: number; y: number }[] = [];

  items.filter((item) => item.outline.length >= 3).forEach((item) => {
    switch (item.kind) {
      case 'room':
        rooms.push({ name: item.text.trim() || null, outline: item.outline });
        break;
      case 'desk': {
        const box = boxOfOutline(item.outline, aspect);
        desks.push({
          x: box.centre.x,
          y: box.centre.y,
          rotationDegrees: planDegreesOf(box.imageDegrees),
          width: round(box.length),
          depth: round(box.breadth),
        });
        break;
      }
      case 'wall': {
        const box = boxOfOutline(item.outline, aspect);
        const [cx, cy] = toSpace(box.centre, aspect);
        const [ux, uy] = [Math.cos(box.imageDegrees * RADIANS) * (box.length / 2), Math.sin(box.imageDegrees * RADIANS) * (box.length / 2)];
        walls.push({
          from: fromSpace([cx - ux, cy - uy], aspect),
          to: fromSpace([cx + ux, cy + uy], aspect),
          thickness: item.thick ? round(box.breadth) : null,
          kind: item.wallKind ?? 'partition',
        });
        break;
      }
      case 'label': {
        const xs = item.outline.map((point) => point.x);
        const ys = item.outline.map((point) => point.y);
        const centre = centreOf(item.outline);
        labels.push({
          text: item.text,
          x: centre.x,
          y: centre.y,
          box: {
            left: Math.min(...xs), top: Math.min(...ys), right: Math.max(...xs), bottom: Math.max(...ys),
          },
        });
        break;
      }
      default: {
        areas.push({ kind: item.kind as SketchAreaKind, outline: item.outline });
        if (item.kind === 'stairs') {
          icons.push({ iconName: 'stairs', ...centreOf(item.outline) });
        } else if (isSymbolKind(item.kind)) {
          icons.push({ iconName: SYMBOL_ICONS[item.kind], ...centreOf(item.outline) });
        }
      }
    }
  });

  return {
    ...base,
    isFloorPlan: true,
    rooms,
    desks,
    walls,
    areas,
    labels,
    icons,
  };
};

// ---------------------------------------------------------------- working on several at once

/** The items whose ids are in `ids`, changed by `change`; the rest as they are. */
const changing = (
  items: readonly ReviewItem[],
  ids: ReadonlySet<string>,
  change: (item: ReviewItem) => ReviewItem,
): ReviewItem[] => items.map((item) => (ids.has(item.id) ? change(item) : item));

/** Somebody moved, turned or resized it: it is theirs now, not a guess to check. */
const confirmed = (item: ReviewItem): ReviewItem => ({ ...item, confidence: undefined });

export const withKindFor = (items: readonly ReviewItem[], ids: ReadonlySet<string>, kind: ReviewKind): ReviewItem[] => changing(
  items, ids, (item) => withKind(item, kind),
);

export const withoutItems = (items: readonly ReviewItem[], ids: ReadonlySet<string>): ReviewItem[] => items
  .filter((item) => !ids.has(item.id));

/**
 * Everything the tracer found taken away, corrected or not, and what the administrator marked
 * themselves kept: to start over by hand where the picture was read badly.
 */
export const withoutDetected = (items: readonly ReviewItem[], detected: readonly ReviewItem[]): ReviewItem[] => (
  withoutItems(items, new Set(detected.map((item) => item.id))));

/**
 * The tracer's finds put back where they have been taken away, as it found them — all of them, or
 * only those it found as `kind`. A find still there is left as corrected, so restoring never undoes
 * a correction.
 *
 * Each goes back in its own place, the tracer's finds first in the order it found them and what
 * was marked or copied by hand after: the order is the order they are drawn in, so a room put
 * back at the end would lie over the desks in it and take their clicks.
 */
export const withDetected = (
  items: readonly ReviewItem[],
  detected: readonly ReviewItem[],
  kind?: ReviewKind,
): ReviewItem[] => {
  const current = new Map(items.map((item) => [item.id, item]));
  const detectedIds = new Set(detected.map((item) => item.id));
  return [
    ...detected.flatMap((item) => {
      const there = current.get(item.id);
      if (there) return [there];
      return kind === undefined || item.kind === kind ? [item] : [];
    }),
    ...items.filter((item) => !detectedIds.has(item.id)),
  ];
};

/**
 * The tracer's finds of one kind taken away, all at once — every desk it found, say — with what
 * the administrator marked themselves kept, as {@link withoutDetected} keeps it. The kind is the
 * one each find has now: a find the tracer took for a desk and the administrator made furniture
 * is furniture, and clearing the desks leaves it.
 */
export const withoutDetectedOf = (
  items: readonly ReviewItem[],
  detected: readonly ReviewItem[],
  kind: ReviewKind,
): ReviewItem[] => {
  const detectedIds = new Set(detected.map((item) => item.id));
  return items.filter((item) => !(detectedIds.has(item.id) && item.kind === kind));
};

/** Of one kind, how many of the tracer's finds are on the plan, and how many restoring puts back. */
export interface DetectedOfKind {
  readonly kind: ReviewKind;
  /** The tracer's finds that are this kind now. */
  readonly left: number;
  /** The tracer's finds of this kind that have been taken away. */
  readonly missing: number;
}

/**
 * Every kind the tracer found or that its finds have been made since, in the order kinds are
 * offered: what clearing and restoring one kind at a time would each change.
 */
export const detectedByKind = (items: readonly ReviewItem[], detected: readonly ReviewItem[]): DetectedOfKind[] => {
  const detectedIds = new Set(detected.map((item) => item.id));
  const present = new Set(items.map((item) => item.id));
  const left = new Map<ReviewKind, number>();
  const missing = new Map<ReviewKind, number>();
  items.filter((item) => detectedIds.has(item.id))
    .forEach((item) => left.set(item.kind, (left.get(item.kind) ?? 0) + 1));
  detected.filter((item) => !present.has(item.id))
    .forEach((item) => missing.set(item.kind, (missing.get(item.kind) ?? 0) + 1));
  return REVIEW_KINDS
    .map((kind) => ({ kind, left: left.get(kind) ?? 0, missing: missing.get(kind) ?? 0 }))
    .filter((entry) => entry.left + entry.missing > 0);
};

/**
 * In the order to draw them: rooms underneath, everything else over them as listed. A room drawn
 * over a desk would take the clicks meant for the desk, its fill faint as it is.
 */
export const inDrawingOrder = (items: readonly ReviewItem[]): ReviewItem[] => [
  ...items.filter((item) => item.kind === 'room'),
  ...items.filter((item) => item.kind !== 'room'),
];

/** How many of the tracer's finds are still there. */
export const detectedCountOf = (items: readonly ReviewItem[], detected: readonly ReviewItem[]): number => {
  const ids = new Set(detected.map((item) => item.id));
  return items.filter((item) => ids.has(item.id)).length;
};

/** Moved by `dx` across and `dy` down, in sketch coordinates. */
export const movedItems = (items: readonly ReviewItem[], ids: ReadonlySet<string>, dx: number, dy: number): ReviewItem[] => changing(
  items,
  ids,
  (item) => confirmed({
    ...item,
    outline: item.outline.map((point) => ({ x: round(point.x + dx), y: round(point.y + dy) })),
  }),
);

/** Each turned by `degrees` (clockwise as drawn) round its own middle. */
export const rotatedItems = (
  items: readonly ReviewItem[],
  ids: ReadonlySet<string>,
  degrees: number,
  aspect: number,
): ReviewItem[] => changing(items, ids, (item) => {
  const [cx, cy] = toSpace(boxOfOutline(item.outline, aspect).centre, aspect);
  const [cos, sin] = [Math.cos(degrees * RADIANS), Math.sin(degrees * RADIANS)];

  return confirmed({
    ...item,
    outline: item.outline.map((point) => {
      const [x, y] = toSpace(point, aspect);
      return fromSpace([cx + ((x - cx) * cos) - ((y - cy) * sin), cy + ((x - cx) * sin) + ((y - cy) * cos)], aspect);
    }),
  });
});

/** Each grown (or shrunk, below 1) by `factor` round its own middle. */
export const scaledItems = (
  items: readonly ReviewItem[],
  ids: ReadonlySet<string>,
  factor: number,
  aspect: number,
): ReviewItem[] => changing(items, ids, (item) => {
  const [cx, cy] = toSpace(boxOfOutline(item.outline, aspect).centre, aspect);

  return confirmed({
    ...item,
    outline: item.outline.map((point) => {
      const [x, y] = toSpace(point, aspect);
      return fromSpace([cx + ((x - cx) * factor), cy + ((y - cy) * factor)], aspect);
    }),
  });
});

/**
 * The item stretched to `length` by `breadth` (lengths of `x`) along its own sides, where it is
 * and turned as it is: a desk set to its real size. The outline keeps its shape — a round table
 * stays round, a door keeps its swing — rather than being redrawn as a rectangle.
 */
export const resizedItem = (item: ReviewItem, length: number, breadth: number, aspect: number): ReviewItem => {
  const box = boxOfOutline(item.outline, aspect);
  const [cx, cy] = toSpace(box.centre, aspect);
  const [ux, uy] = [Math.cos(box.imageDegrees * RADIANS), Math.sin(box.imageDegrees * RADIANS)];
  const alongFactor = box.length > 0 ? Math.max(1, length) / box.length : 1;
  const acrossFactor = box.breadth > 0 ? Math.max(1, breadth) / box.breadth : 1;

  return confirmed({
    ...item,
    outline: item.outline.map((point) => {
      const [x, y] = toSpace(point, aspect);
      // Into the box's own axes, stretched, and back.
      const u = ((x - cx) * ux) + ((y - cy) * uy);
      const v = ((y - cy) * ux) - ((x - cx) * uy);
      const [su, sv] = [u * alongFactor, v * acrossFactor];
      return fromSpace([cx + (su * ux) - (sv * uy), cy + (su * uy) + (sv * ux)], aspect);
    }),
  });
};

/** Copies of the items, a little down and to the right, with ids of their own. */
export const duplicatesOf = (items: readonly ReviewItem[], ids: ReadonlySet<string>, offset = 12): ReviewItem[] => items
  .filter((item) => ids.has(item.id))
  .map((item) => ({
    ...item,
    id: newId(),
    confidence: undefined,
    outline: item.outline.map((point) => ({ x: round(point.x + offset), y: round(point.y + offset) })),
  }));

/** The items whose middle lies in the box from `from` to `to`: what a drag round them selects. */
export const itemsInBox = (items: readonly ReviewItem[], from: SketchPoint, to: SketchPoint): string[] => {
  const [left, right] = [Math.min(from.x, to.x), Math.max(from.x, to.x)];
  const [top, bottom] = [Math.min(from.y, to.y), Math.max(from.y, to.y)];

  return items
    .filter((item) => {
      const centre = centreOf(item.outline);
      return centre.x >= left && centre.x <= right && centre.y >= top && centre.y <= bottom;
    })
    .map((item) => item.id);
};

/** The items like this one: the same kind, and within a fifth of its size either way. */
export const similarTo = (items: readonly ReviewItem[], id: string, aspect: number): string[] => {
  const model = items.find((item) => item.id === id);
  if (!model) return [];

  const box = boxOfOutline(model.outline, aspect);
  const near = (a: number, b: number): boolean => b > 0 && a / b >= 0.8 && a / b <= 1.25;

  return items
    .filter((item) => item.kind === model.kind)
    .filter((item) => {
      const other = boxOfOutline(item.outline, aspect);
      return near(other.length, box.length) && near(other.breadth || 1, box.breadth || 1);
    })
    .map((item) => item.id);
};
